import { IOption } from '@cmps/ng-select';

import { TemplateConfig } from '../../core/config.scorecard.model';
import { Pillar } from '../../core/definition.scorecard.model';
import { GridType } from '../../core/template-definition.model';
import {
    Cell,
    CellNode,
    Column,
    flattenArrayWithObj,
    Row,
    RowNode,
    Sort,
    SortOrder,
    sortOrderWithDefault,
} from '../../experimental.grid';
import { TranslationKeyMap } from '../../scorecard.i18n';
import { createEmpty, RankTypes, ScorecardDto } from '../../scorecard.model';

import {
    CellContext,
    ColumnContext,
    DirectReportContributionDisplay,
    DirectReportContributionMetric,
    DirectReportContributionResult,
    retrieveContributionCellData,
    RowContext,
} from './direct-report-contribution-grid.model';
import {
    DirectReportRankDisplay,
    DirectReportRankResult,
    RankCellContext,
    RankColumnContext,
    RankColumnType,
} from './direct-report-rank-grid.model';
import {
    DirectReportScorecardDisplay,
    DirectReportScorecardResult,
    DRScorecardMetric,
    retrieveCellData,
} from './direct-report-scorecard-grid.model';
import { FilterCategory } from './filter-category.model';
import {
    ColumnType,
    DrsColumnType,
    FilterPillar,
    TemplateID,
} from './team-grid.model';
import {
    DirectReportContributionGrid,
    DirectReportRankGrid,
    DirectReportScorecardGrid,
    IOptionScorecard,
} from './team-grid.state';

export const dedup = <T>(
    arr: T[],
    lookupKey: (value: T) => string | number,
): T[] => {
    if (!arr) {
        return arr;
    }
    const lookup = {};
    return arr.reduce((p, c) => {
        const key = lookupKey(c);
        if (!lookup[key]) {
            lookup[key] = true;
            p.push(c);
        }
        return p;
    }, []);
};

function sort<T>(arr: T[], key: string): T[] {
    const ret = arr ? [...arr] : [];
    ret.sort((a, b) => a[key] - b[key]);
    return ret;
}

function distillContributionRows(rows: Array<Row<RowContext>>): {
    rowsTop: Array<Row<RowContext>>;
    rows: Array<Row<RowContext>>;
} {
    const nextRowsTop = [];
    const nextRows = [];
    rows.forEach((r) => {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (
            // eslint-disable-next-line @typescript-eslint/dot-notation
            r.cells.findIndex((c) => c.ctx.data[0]['recordType'] === 'M') !== -1
        ) {
            nextRowsTop.push(r);
        } else {
            nextRows.push(r);
        }
    });
    return { rowsTop: nextRowsTop, rows: nextRows };
}

function makeRows(
    tplConfig: TemplateConfig,
    columns: Column[],
    data: DirectReportContributionDisplay[],
): Array<Row<RowContext>> {
    const height = 30;
    const rows: Array<Row<RowContext>> = [];
    let cells: Array<Cell<CellContext>> = [];

    data.forEach((d, i) => {
        cells = [];
        columns.forEach((c) => {
            cells.push(
                ...c.children.map((cc) => ({
                    columnId: cc.id,
                    type: c.ctx.pillarId as ColumnType,
                    height,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        data: [d],
                        pillarId: c.ctx.pillarId,
                    },
                })),
            );
        });
        rows.push({
            id: i.toString(),
            height,
            cells,
        });
    });
    return rows;
}

function generateDRContributionMetric(
    data: DirectReportContributionResult[],
): DirectReportContributionMetric[] {
    const result: DirectReportContributionMetric[] = [];
    data.forEach((d) => {
        const nDRContributionMetric: DirectReportContributionMetric = {
            pillarId: d.pillarId,
            metricId: d.metricId,
            metricOrder: d.metricOrder,
            metricName: d.metricName,
            actual: d.actual,
            target: d.target,
            targetPercent: d.targetPercent,
        };
        result.push(nDRContributionMetric);
    });
    return result;
}

function makeColumnsRank(
    data: DirectReportRankResult[],
): Array<Column<RankColumnContext>> {
    const hasRor = data.find(
        (f) =>
            f.rankType === RankTypes.ROLE_OVERALL &&
            f.maxRank !== null &&
            f.myRank !== null,
    );
    const hasSgo = data.find(
        (f) =>
            f.rankType === RankTypes.SUPER_GROUP_OVERALL &&
            f.maxRank !== null &&
            f.myRank !== null,
    );
    const hasSSgo = data.find(
        (f) =>
            f.rankType === RankTypes.SITE_SUPERGROUP &&
            f.maxRank !== null &&
            f.myRank !== null,
    );
    const hasRbr = data.find(
        (f) =>
            f.rankType === RankTypes.ROLE_BY_REGION &&
            f.maxRank !== null &&
            f.myRank !== null,
    );
    const hasPg = data.find(
        (f) =>
            f.rankType === RankTypes.PEER_GROUP_OVERALL &&
            f.maxRank !== null &&
            f.myRank !== null,
    );
    const hasLob = data.find(
        (f) =>
            f.rankType === RankTypes.LOB_OVERALL &&
            f.maxRank !== null &&
            f.myRank !== null,
    );
    const columns: Array<Column<RankColumnContext>> = [
        {
            id: RankColumnType.DIRECT_REPORTS.toString(),
            compareFn: compareRank,
            sticky: 'left',
            ctx: {
                templateId: TemplateID.HEADER,
                type: RankColumnType.DIRECT_REPORTS,
            },
        },
        {
            id: RankColumnType.SUPERGROUP_NAME.toString(),
            compareFn: compareRank,
            ctx: {
                templateId: TemplateID.HEADER,
                type: RankColumnType.SUPERGROUP_NAME,
            },
        },
        {
            id: RankColumnType.PERIOD.toString(),
            compareFn: compareRank,
            ctx: { templateId: TemplateID.HEADER, type: RankColumnType.PERIOD },
        },
        {
            id: RankColumnType.ADJUSTED_POINTS.toString(),
            compareFn: compareRank,
            ctx: {
                templateId: TemplateID.HEADER,
                type: RankColumnType.ADJUSTED_POINTS,
            },
        },
        {
            id: RankColumnType.SUPERGROUP_OVERALL_RANK.toString(),
            compareFn: compareRank,
            hidden: hasSgo === undefined,
            ctx: {
                templateId: TemplateID.HEADER,
                type: RankColumnType.SUPERGROUP_OVERALL_RANK,
            },
        },
        {
            id: RankColumnType.SITE_SUPERGROUP_RANK.toString(),
            compareFn: compareRank,
            hidden: hasSSgo === undefined,
            ctx: {
                templateId: TemplateID.HEADER,
                type: RankColumnType.SITE_SUPERGROUP_RANK,
            },
        },
        {
            id: RankColumnType.ROLE_OVERALL_RANK.toString(),
            compareFn: compareRank,
            hidden: hasRor === undefined,
            ctx: {
                templateId: TemplateID.HEADER,
                type: RankColumnType.ROLE_OVERALL_RANK,
            },
        },
        {
            id: RankColumnType.ROLE_BY_REGION_RANK.toString(),
            compareFn: compareRank,
            hidden: hasRbr === undefined,
            ctx: {
                templateId: TemplateID.HEADER,
                type: RankColumnType.ROLE_BY_REGION_RANK,
            },
        },
        {
            id: RankColumnType.PEER_GROUP_OVERALL_RANK.toString(),
            compareFn: compareRank,
            hidden: hasPg === undefined,
            ctx: {
                templateId: TemplateID.HEADER,
                type: RankColumnType.PEER_GROUP_OVERALL_RANK,
            },
        },
        {
            id: RankColumnType.LOB_OVERALL_RANK.toString(),
            compareFn: compareRank,
            hidden: hasLob === undefined,
            ctx: {
                templateId: TemplateID.HEADER,
                type: RankColumnType.LOB_OVERALL_RANK,
            },
        },
    ];
    return columns;
}

function makeRowsRank(
    columns: Column[],
    data: DirectReportRankResult[],
): Array<Row<RowContext>> {
    const height = 30;
    const rows: Array<Row<RowContext>> = [];
    const unique = Array<string>();

    for (const d of data) {
        const key = `${d.subUserId}**${d.subSuperGroupId}**${d.subStartDate}**${d.subEndDate}`;
        if (!unique.includes(key)) {
            unique.push(key);
        }
    }

    const drRankDisplay = Array<DirectReportRankDisplay>();
    unique.forEach((u) => {
        const params = u.split('**');
        const filtered = data.filter(
            (d) =>
                d.subUserId.toString() === params[0] &&
                d.subSuperGroupId.toString() === params[1] &&
                d.subStartDate.toString() === params[2] &&
                d.subEndDate.toString() === params[3],
        );

        const sgorArray = filtered.filter(
            (f) => f.rankType === RankTypes.SUPER_GROUP_OVERALL,
        );
        const ssgorArray = filtered.filter(
            (f) => f.rankType === RankTypes.SITE_SUPERGROUP,
        );
        const rorArray = filtered.filter(
            (f) => f.rankType === RankTypes.ROLE_OVERALL,
        );
        const rbrArray = filtered.filter(
            (f) => f.rankType === RankTypes.ROLE_BY_REGION,
        );
        const prArray = filtered.filter(
            (f) => f.rankType === RankTypes.PEER_GROUP_OVERALL,
        );
        const lobArray = filtered.filter(
            (f) => f.rankType === RankTypes.LOB_OVERALL,
        );

        const sDate = new Date(filtered[0].subStartDate);
        const eDate = new Date(filtered[0].subEndDate);
        const p = `${
            sDate.getMonth() + 1
        }-${sDate.getDate()}-${sDate.getFullYear()} / ${
            eDate.getMonth() + 1
        }-${eDate.getDate()}-${eDate.getFullYear()}`;

        const sgorVal =
            sgorArray.length > 0 && sgorArray[0].myRank && sgorArray[0].maxRank
                ? `${sgorArray[0].myRank}/${sgorArray[0].maxRank}`
                : null;
        const ssgorVal =
            ssgorArray.length > 0 &&
            ssgorArray[0].myRank &&
            ssgorArray[0].maxRank
                ? `${ssgorArray[0].myRank}/${ssgorArray[0].maxRank}`
                : null;
        const rorVal =
            rorArray.length > 0 && rorArray[0].myRank && rorArray[0].maxRank
                ? `${rorArray[0].myRank}/${rorArray[0].maxRank}`
                : null;
        const rbrVal =
            rbrArray.length > 0 && rbrArray[0].myRank && rbrArray[0].maxRank
                ? `${rbrArray[0].myRank}/${rbrArray[0].maxRank}`
                : null;
        const prVal =
            prArray.length > 0 && prArray[0].myRank && prArray[0].maxRank
                ? `${prArray[0].myRank}/${prArray[0].maxRank}`
                : null;
        const lobVal =
            lobArray.length > 0 && lobArray[0].myRank && lobArray[0].maxRank
                ? `${lobArray[0].myRank}/${lobArray[0].maxRank}`
                : null;

        const sgor = sgorArray.length > 0 ? sgorVal : null;
        const ssgor = ssgorArray.length > 0 ? ssgorVal : null;
        const ror = rorArray.length > 0 ? rorVal : null;
        const rbr = rbrArray.length > 0 ? rbrVal : null;
        const pr = prArray.length > 0 ? prVal : null;
        const lob = lobArray.length > 0 ? lobVal : null;

        drRankDisplay.push({
            subScorecardId: filtered[0].subScorecardId,
            subUserId: filtered[0].subUserId,
            directReport: filtered[0].subUserName,
            subSuperGroupName: filtered[0].subSuperGroupName,
            subSuperGroupId: filtered[0].subSuperGroupId,
            startDate: filtered[0].subStartDate,
            endDate: filtered[0].subEndDate,
            period: p,
            overallScore: filtered[0].subAdjustedPoints
                ? filtered[0].subAdjustedPoints
                : null,
            rankType: filtered[0].rankType,
            myRank: filtered[0].myRank ? filtered[0].myRank : null,
            superGroupOverallRank: sgor,
            siteSuperGroupRank: ssgor,
            roleOverallRank: ror,
            roleByRegionRank: rbr,
            peerGroupOverallRank: pr,
            lobOverallRank: lob,
            sortDefs: filtered[0].sortDefs,
        });
    });
    drRankDisplay.forEach((d, j) => {
        const cells: Array<Cell<RankCellContext>> = [];
        columns.forEach((c, i) => {
            cells.push({
                columnId: c.id,
                height,
                ctx: {
                    templateId: TemplateID.CONTENT,
                    data: d,
                },
            });
        });
        rows.push({
            id: `${Math.random()}`,
            height,
            cells,
        });
    });
    return rows;
}

export function syncContributionGrid(
    tplConfig: TemplateConfig,
    header: ScorecardDto,
    data: DirectReportContributionResult[],
    sortDefs: Sort[],
): DirectReportContributionGrid {
    header = header || createEmpty();
    const nextColumns = makecolumns(tplConfig, data);
    const clearData = clearContributionGridData(data);
    let nextRows = makeRows(tplConfig, nextColumns, clearData);
    nextRows = applyDefaultContributionSorting(nextRows);
    const newFilterDef = generateSGList(nextRows, GridType.Contribution_View);
    const nextRowsPair = distillContributionRows(nextRows);
    const nextFilterPillar = generateMetricPillarList(nextColumns);
    if (!sortDefs) {
        sortDefs = [];
    }
    return {
        scorecardId: header.scorecardId,
        superGroupName: header.groupName,
        columns: nextColumns,
        ...nextRowsPair,
        sortDefs,
        filterDef: newFilterDef,
        filterPillar: nextFilterPillar,
        defaultColumnIdOrder: nextColumns.map((e) => e.id),
    };
}

export function syncRankGrid(
    tplConfig: TemplateConfig,
    header: ScorecardDto,
    data: DirectReportRankResult[],
    sortDefs: Sort[],
): DirectReportRankGrid {
    const nextColumns = makeColumnsRank(data);
    let nextRows = makeRowsRank(nextColumns, data);
    if (!sortDefs) {
        sortDefs = [];
    }
    nextRows = applyDefaultRankSorting(nextRows);
    const newFilterDef = generateSGList(nextRows, GridType.Rank_View);
    return {
        columns: nextColumns,
        rows: nextRows,
        sortDefs,
        scorecardId: header.scorecardId,
        filterDef: newFilterDef,
    };
}

export function sortPerformanceGrid(
    columnId: string,
    columns: Array<Column<ColumnContext>>,
    rows: Array<Row<RowContext>>,
    sortDefs: Sort[],
): { rows: Array<Row<RowContext>>; sortDefs: Sort[] } {
    const column = columns.find((e) => e.id === columnId);
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const sortDef = sortDefs && sortDefs.length ? sortDefs[0] : null;
    const defaultSortDef = {
        columnId: ColumnType.PILLAR.toString(),
        order: SortOrder.ASC,
    };
    const nextSortDefs = sortOrderWithDefault(
        columns,
        column,
        sortDef,
        defaultSortDef,
    );
    return { rows: [...rows], sortDefs: nextSortDefs };
}

export function expandAllColumns(
    expand: boolean,
    columns: Array<Column<ColumnContext>>,
): void {
    if (columns.some((c) => c.hidden === false)) {
        columns
            .filter((c) => c.hidden === false)
            .forEach((c) => {
                c.children.forEach((cc) => {
                    if (cc.ctx.isExpandable) {
                        cc.hidden = false;
                        if (cc.ctx.hasExpandableSibling) {
                            cc.ctx.isExplicitlyShown = !expand;
                        }
                    } else {
                        cc.hidden = !expand;
                        cc.ctx.isExplicitlyShown = expand;
                    }
                });
            });
    } else {
        const flat = flattenArrayWithObj(columns, 'children');
        flat.forEach((e) => {
            if (e.ctx.hasExpandableSibling) {
                e.hidden = !expand;
                e.ctx.isExplicitlyShown = expand;
            } else {
                e.ctx.isColumnExpanded = expand;
            }
        });
    }
}

export function expandMetricColumns(
    columnId: string,
    columns: Array<Column<ColumnContext>>,
): void {
    columns.forEach((c) => {
        c.children.forEach((cc) => {
            if (cc.id.includes(columnId) && cc.id !== columnId) {
                cc.hidden = !cc.hidden;
                if (cc.ctx.hasExpandableSibling) {
                    cc.ctx.isExplicitlyShown = !cc.hidden;
                }
            }
        });
    });
}

export function generateSGList(
    rows: Array<Row<RowContext>>,
    gridType: GridType,
): IOption[] {
    const listedSG: { [id: number]: IOption } = {};
    rows.forEach((r) => {
        r.cells.forEach((c) => {
            if (gridType === GridType.Contribution_View) {
                listedSG[c.ctx.data[0].superGroupId] = {
                    value: c.ctx.data[0].superGroupId,
                    label: c.ctx.data[0].superGroupName || '',
                };
            } else if (gridType === GridType.Direct_Reports_View) {
                listedSG[c.ctx.data[0].superGroupId] = {
                    value: c.ctx.data[0].superGroupId,
                    label: c.ctx.data[0].superGroup || '',
                };
            } else if (gridType === GridType.Rank_View) {
                listedSG[c.ctx.data.subSuperGroupId] = {
                    value: c.ctx.data.subSuperGroupId,
                    label: c.ctx.data.subSuperGroupName || '',
                };
            }
        });
    });
    return Object.values(listedSG).sort((a, b) =>
        a.label.localeCompare(b.label),
    );
}

export function generateMetricPillarList(
    columns: Array<Column<ColumnContext>>,
): FilterPillar[] {
    const pillarFilterItems: FilterPillar[] = [];
    // eslint-disable-next-line array-callback-return
    columns.map((col) => {
        if (!col.ctx.pillarName) {
            // eslint-disable-next-line array-callback-return
            return;
        }
        pillarFilterItems.push({
            name: col.ctx.pillarName,
            isSelected: false,
            columnId: col.id,
        });
    });
    return pillarFilterItems;
}

// TODO move. tl. translations
interface ShallowPillar {
    identifier: string;
    name: string;
}

// TODO move. tl. translations
const pseudoPillars: ShallowPillar[] = [
    {
        identifier: String(DrsColumnType.WEIGHTED_METRICS),
        name: TranslationKeyMap._9898_WEIGHTED_METRICS,
    },
    {
        identifier: String(DrsColumnType.FYI_METRICS),
        name: TranslationKeyMap._9894_FYI_METRICS,
    },
    {
        identifier: String(DrsColumnType.CONDITION_METRICS),
        name: TranslationKeyMap._9895_CONDITION_METRICS,
    },
    {
        identifier: String(DrsColumnType.MODIFIER_METRICS),
        name: TranslationKeyMap._9896_MODIFIER_METRICS,
    },
    {
        identifier: String(DrsColumnType.QUALIFIER_METRICS),
        name: TranslationKeyMap._9897_QUALIFIER_METRICS,
    },
];

function makecolumns(
    tplConfig: TemplateConfig,
    directReportCont: DirectReportContributionResult[],
): Array<Column<ColumnContext>> {
    const height = 40;

    /* stickied columns need to be separate because reconstructColumns() doesn't handle single stickied child column
    while the siblings and parent are not */
    const columns: Array<Column<ColumnContext>> = [
        {
            id: '',
            height,
            sticky: 'left',
            ctx: {
                templateId: TemplateID.HEADER_GROUP,
                type: '' as any as ColumnType,
            },
            children: [
                {
                    id: ColumnType.USER_NAME.toString(),
                    compareFn: compareContribution,
                    sticky: 'left',
                    ctx: {
                        templateId: TemplateID.HEADER,
                        type: ColumnType.USER_NAME,
                        metricName: TranslationKeyMap._9128_DIRECTREPORTS,
                    },
                },
            ],
        },
        {
            id: '2',
            height,
            ctx: {
                templateId: TemplateID.HEADER_GROUP,
                type: '' as any as ColumnType,
            },
            children: [
                {
                    id: ColumnType.SUPERGROUP_NAME.toString(),
                    compareFn: compareContribution,
                    ctx: {
                        templateId: TemplateID.HEADER,
                        type: ColumnType.SUPERGROUP_NAME,
                        metricName: TranslationKeyMap._67_SUPERGROUP,
                    },
                },
                {
                    id: ColumnType.PERIOD.toString(),
                    ctx: {
                        templateId: TemplateID.HEADER,
                        type: ColumnType.PERIOD,
                        metricName: TranslationKeyMap._773_PERIOD,
                    },
                },
            ],
        },
    ];

    const foundPseudoPillars: Pillar[] = [];
    directReportCont.forEach((e) => {
        const pillarIdStr = e.pillarId.toString();
        const pseudoPillar = pseudoPillars.find(
            (ee) => ee.identifier === pillarIdStr,
        );
        if (!pseudoPillar) {
            return;
        }

        const index = foundPseudoPillars.findIndex(
            (p) => p.identifier === pillarIdStr,
        );
        if (index === -1) {
            foundPseudoPillars.push({
                identifier: pseudoPillar.identifier,
                name: pseudoPillar.name,
                shortName: null,
                initials: null,
                clientLanguageTranslationId: null,
                description: null,
                descriptionClientLanguageTranslationId: null,
                displayOrder: e.pillarId,
                metrics: [],
            });
        }
    });

    foundPseudoPillars.forEach((e) => {
        const data = directReportCont.filter(
            (ee) => ee.pillarId.toString() === e.identifier,
        );
        const unique = [];
        const metrics = [];
        data.forEach((d) => {
            if (!unique.includes(d.metricId)) {
                unique.push(d.metricId);
                metrics.push({
                    metricId: d.metricId,
                    displayOrder: d.pillarId,
                    weight: null,
                    displayType: 0,
                    optionalColumns: [],
                    components: [],
                });
            }
        });
        e.metrics = metrics;
    });

    const pillars = sort(
        [...(tplConfig.pillars || []), ...foundPseudoPillars],
        'displayOrder',
    );
    pillars.forEach((ps) => {
        const metrics = sort(ps.metrics, 'displayOrder');
        const metricCoulmns: Array<Column<ColumnContext>> = [];
        metrics.forEach((m) => {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            const dat =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                directReportCont &&
                directReportCont.find(
                    (e) =>
                        e.pillarId.toString() === ps.identifier &&
                        e.metricId === m.metricId,
                );
            if (!dat) {
                return;
            }
            const colId = `${ps.identifier}-${dat.metricId}`;
            metricCoulmns.push({
                id: colId,
                compareFn: compareContribution,
                ctx: {
                    templateId: TemplateID.HEADER,
                    type: ColumnType.ACTUAL,
                    pillarId: ps.identifier,
                    metricId: `${ps.identifier}-${dat.metricId}`,
                    metricName: dat.metricName,
                    isExpandable: true,
                    isColumnExpanded: false,
                },
                hidden: false,
            });
            metricCoulmns.push({
                id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET}`,
                compareFn: compareContribution,
                ctx: {
                    templateId: TemplateID.HEADER,
                    type: ColumnType.TARGET,
                    metricId: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET}`,
                    pillarId: ps.identifier,
                    metricName: TranslationKeyMap._726_TARGET,
                    expandableSiblingColumnId: colId,
                    hasExpandableSibling: true,
                },
                hidden: true,
            });
            metricCoulmns.push({
                id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET_PERCENT}`,
                compareFn: compareContribution,
                ctx: {
                    templateId: TemplateID.HEADER,
                    type: ColumnType.TARGET_PERCENT,
                    metricId: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET_PERCENT}`,
                    pillarId: ps.identifier,
                    metricName: TranslationKeyMap._768___TO_TARGET,
                    expandableSiblingColumnId: colId,
                    hasExpandableSibling: true,
                },
                hidden: true,
            });
        });
        if (metricCoulmns.length > 0) {
            columns.push({
                id: ps.name.toString(),
                height,
                ctx: {
                    templateId: TemplateID.HEADER_GROUP,
                    type: ps.name as any as ColumnType,
                    pillarId: ps.identifier,
                    pillarName: ps.name,
                },
                children: metricCoulmns,
            });
        }
    });

    return columns;
}

function compareContribution(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    return compareDefContribution(
        cellA.def,
        cellB.def,
        rowA.def,
        rowB.def,
        def,
    );
}

function compareScorecard(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    return compareDefScorecard(cellA.def, cellB.def, rowA.def, rowB.def, def);
}

// TODO refactor
function compareDefContribution(
    cellA: Cell<CellContext>,
    cellB: Cell<CellContext>,
    rowA: Row<RowContext, CellContext>,
    rowB: Row<RowContext, CellContext>,
    def: Sort,
): number {
    try {
        const type = parseInt(def.columnId);
        const strColumns = [
            ColumnType.USER_NAME,
            ColumnType.SUPERGROUP_NAME,
            ColumnType.PERIOD,
        ];
        const strColumnIds = ['pointbands', 'metricrank'];
        const columnIdLower = def.columnId.toLowerCase();
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        const isStringCompare =
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            strColumns.indexOf(type) !== -1 ||
            strColumnIds.findIndex((e) => columnIdLower.includes(e)) !== -1;
        const mode: 'number' | 'string' = isStringCompare ? 'string' : 'number';
        const ctxA: CellContext = cellA.ctx;
        const ctxB: CellContext = cellB.ctx;
        let valA: any;
        let valB: any;

        switch (type) {
            case ColumnType.USER_NAME:
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valA = ctxA.data[0]['userName'].split(' ')[0];
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valB = ctxB.data[0]['userName'].split(' ')[0];
                break;
            case ColumnType.SUPERGROUP_NAME:
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valA = ctxA.data[0]['superGroupName'];
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valB = ctxB.data[0]['superGroupName'];
                break;
            case ColumnType.PERIOD:
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valA = ctxA.data[0]['period'].split('/')[0];
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valB = ctxB.data[0]['period'].split('/')[0];
                break;
            default:
                // eslint-disable-next-line no-case-declarations
                const metricTypeA = cellA.columnId;
                // eslint-disable-next-line no-case-declarations
                const metricTypeB = cellB.columnId;
                valA = Number(
                    retrieveContributionCellData(ctxA.data, metricTypeA),
                );
                valB = Number(
                    retrieveContributionCellData(ctxB.data, metricTypeB),
                );
                break;
        }
        if (mode === 'number') {
            valA = valA == null || valA === '-' || isNaN(valA) ? null : valA;
            valB = valB == null || valB === '-' || isNaN(valB) ? null : valB;
        } else {
            valA = valA == null ? '' : valA;
            valB = valB == null ? '' : valB;
        }
        if (mode === 'number') {
            if (valA === valB) {
                return 0;
            } else if (valA === null) {
                return 1;
            } else if (valB === null) {
                return -1;
            } else if (def.order === SortOrder.ASC) {
                return valA < valB ? -1 : 1;
            } else {
                return valA < valB ? 1 : -1;
            }
        } else {
            if (def.order === SortOrder.ASC) {
                return valA.localeCompare(valB);
            } else {
                return valB.localeCompare(valA);
            }
        }
    } catch (e) {
        console.log(e);
        return Number.MIN_SAFE_INTEGER;
    }
}

// TODO refactor
export function compareDefScorecard(
    cellA: Cell<CellContext>,
    cellB: Cell<CellContext>,
    rowA: Row<RowContext, CellContext>,
    rowB: Row<RowContext, CellContext>,
    def: Sort,
): number {
    try {
        const type = parseInt(def.columnId);
        const strColumns = [
            ColumnType.USER_NAME,
            ColumnType.SUPERGROUP_NAME,
            ColumnType.PERIOD,
        ];
        const selectedColumnId = def.columnId.split('-');
        const colMetricId =
            selectedColumnId.length > 2
                ? Number(selectedColumnId[2])
                : Number(selectedColumnId[0]);
        const strColumnIds = [
            ColumnType.ISINEFFECT,
            ColumnType.METRIC_RANK,
            ColumnType.QUALIFIERMET,
            ColumnType.POINTS_BAND,
        ]; // TODO const/enum
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        const isStringCompare =
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            strColumns.indexOf(type) !== -1 ||
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            strColumnIds.indexOf(colMetricId) !== -1;
        const mode: 'number' | 'string' = isStringCompare ? 'string' : 'number';
        const ctxA: CellContext = cellA.ctx;
        const ctxB: CellContext = cellB.ctx;
        let valA: any;
        let valB: any;

        switch (type) {
            case ColumnType.USER_NAME:
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valA = ctxA.data[0]['directReport'].split(' ')[0];
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valB = ctxB.data[0]['directReport'].split(' ')[0];
                break;
            case ColumnType.SUPERGROUP_NAME:
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valA = ctxA.data[0]['superGroup'];
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valB = ctxB.data[0]['superGroup'];
                break;
            case ColumnType.PERIOD:
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valA = ctxA.data[0]['period'];
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valB = ctxB.data[0]['period'];
                break;
            case ColumnType.OVERALL_SCORE:
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valA = ctxA.data[0]['overallScore'];
                // eslint-disable-next-line @typescript-eslint/dot-notation
                valB = ctxB.data[0]['overallScore'];
                break;
            default:
                // eslint-disable-next-line no-case-declarations
                const cIds = def.columnId.split('-');
                if (cIds.length < 3) {
                    // eslint-disable-next-line @typescript-eslint/dot-notation
                    valA = Number(
                        retrieveCellData(
                            ctxA.data,
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            cellA['type'],
                            cellA.columnId,
                            'actual',
                        ),
                    );
                    // eslint-disable-next-line @typescript-eslint/dot-notation
                    valB = Number(
                        retrieveCellData(
                            ctxB.data,
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            cellB['type'],
                            cellB.columnId,
                            'actual',
                        ),
                    );
                } else {
                    switch (Number(cIds[2])) {
                        case ColumnType.TARGET:
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valA = Number(
                                retrieveCellData(
                                    ctxA.data,
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    cellA['type'],
                                    cellA.columnId,
                                    'target',
                                ),
                            );
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valB = Number(
                                retrieveCellData(
                                    ctxB.data,
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    cellB['type'],
                                    cellB.columnId,
                                    'target',
                                ),
                            );
                            break;
                        case ColumnType.TARGET_PERCENT:
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valA = Number(
                                retrieveCellData(
                                    ctxA.data,
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    cellA['type'],
                                    cellA.columnId,
                                    'percentToTarget',
                                ),
                            );
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valB = Number(
                                retrieveCellData(
                                    ctxB.data,
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    cellB['type'],
                                    cellB.columnId,
                                    'percentToTarget',
                                ),
                            );
                            break;
                        case ColumnType.POINTS_BAND:
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valA = retrieveCellData(
                                ctxA.data,
                                // eslint-disable-next-line @typescript-eslint/dot-notation
                                cellA['type'],
                                cellA.columnId,
                                'pointBand',
                            );
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valB = retrieveCellData(
                                ctxB.data,
                                // eslint-disable-next-line @typescript-eslint/dot-notation
                                cellB['type'],
                                cellB.columnId,
                                'pointBand',
                            );
                            break;
                        case ColumnType.WEIGHT:
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valA = Number(
                                retrieveCellData(
                                    ctxA.data,
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    cellA['type'],
                                    cellA.columnId,
                                    'weight',
                                ),
                            );
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valB = Number(
                                retrieveCellData(
                                    ctxB.data,
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    cellB['type'],
                                    cellB.columnId,
                                    'weight',
                                ),
                            );
                            break;
                        case ColumnType.ADJUSTED_POINTS:
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valA = Number(
                                retrieveCellData(
                                    ctxA.data,
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    cellA['type'],
                                    cellA.columnId,
                                    'adjustedPoints',
                                ),
                            );
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valB = Number(
                                retrieveCellData(
                                    ctxB.data,
                                    // eslint-disable-next-line @typescript-eslint/dot-notation
                                    cellB['type'],
                                    cellB.columnId,
                                    'adjustedPoints',
                                ),
                            );
                            break;
                        case ColumnType.METRIC_RANK:
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valA = retrieveCellData(
                                ctxA.data,
                                // eslint-disable-next-line @typescript-eslint/dot-notation
                                cellA['type'],
                                cellA.columnId,
                                'rank',
                            );
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valB = retrieveCellData(
                                ctxB.data,
                                // eslint-disable-next-line @typescript-eslint/dot-notation
                                cellB['type'],
                                cellB.columnId,
                                'rank',
                            );
                            break;
                        case ColumnType.ISINEFFECT:
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valA = retrieveCellData(
                                ctxA.data,
                                // eslint-disable-next-line @typescript-eslint/dot-notation
                                cellA['type'],
                                cellA.columnId,
                                'isInEffect',
                            );
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valB = retrieveCellData(
                                ctxB.data,
                                // eslint-disable-next-line @typescript-eslint/dot-notation
                                cellB['type'],
                                cellB.columnId,
                                'isInEffect',
                            );
                            break;
                        case ColumnType.QUALIFIERMET:
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valA = retrieveCellData(
                                ctxA.data,
                                // eslint-disable-next-line @typescript-eslint/dot-notation
                                cellA['type'],
                                cellA.columnId,
                                'isInEffect',
                            );
                            // eslint-disable-next-line @typescript-eslint/dot-notation
                            valB = retrieveCellData(
                                ctxB.data,
                                // eslint-disable-next-line @typescript-eslint/dot-notation
                                cellB['type'],
                                cellB.columnId,
                                'isInEffect',
                            );
                            break;
                        default:
                            break;
                    }
                }
                break;
        }
        if (mode === 'number') {
            valA = valA == null || valA === '-' || isNaN(valA) ? null : valA;
            valB = valB == null || valB === '-' || isNaN(valB) ? null : valB;
        } else {
            valA = valA == null ? '' : valA;
            valB = valB == null ? '' : valB;
        }
        if (mode === 'number') {
            if (valA === valB) {
                return 0;
            } else if (valA === null) {
                return 1;
            } else if (valB === null) {
                return -1;
            } else if (def.order === SortOrder.ASC) {
                return valA < valB ? -1 : 1;
            } else {
                return valA < valB ? 1 : -1;
            }
        } else {
            if (def.order === SortOrder.ASC) {
                return valA.localeCompare(valB);
            } else {
                return valB.localeCompare(valA);
            }
        }
    } catch (e) {
        console.log(e);
        return Number.MIN_SAFE_INTEGER;
    }
}

function compareRank(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    return compareDefRank(cellA.def, cellB.def, rowA.def, rowB.def, def);
}

// TODO refactor
function compareDefRank(
    cellA: Cell<RankCellContext>,
    cellB: Cell<RankCellContext>,
    rowA: Row<RowContext, RankCellContext>,
    rowB: Row<RowContext, RankCellContext>,
    def: Sort,
): number {
    try {
        const type = parseInt(def.columnId);
        const strColumns = [
            RankColumnType.SUPERGROUP_NAME,
            RankColumnType.DIRECT_REPORTS,
            RankColumnType.PERIOD,
        ];
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        const mode: 'number' | 'string' =
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            strColumns.indexOf(type) !== -1 ? 'string' : 'number';
        const ctxA: RankCellContext = cellA.ctx;
        const ctxB: RankCellContext = cellB.ctx;
        let valA: any;
        let valB: any;
        switch (type) {
            case RankColumnType.ADJUSTED_POINTS:
                valA = ctxA.data.overallScore;
                valB = ctxB.data.overallScore;
                break;
            case RankColumnType.RANK:
                valA = ctxA.data.rankType;
                valB = ctxB.data.rankType;
                break;
            case RankColumnType.MY_RANK:
                valA = ctxA.data.myRank;
                valB = ctxB.data.myRank;
                break;
            case RankColumnType.SUPERGROUP_NAME:
                valA = ctxA.data.subSuperGroupName;
                valB = ctxB.data.subSuperGroupName;
                break;
            case RankColumnType.DIRECT_REPORTS:
                valA = ctxA.data.directReport.split(' ')[0];
                valB = ctxB.data.directReport.split(' ')[0];
                break;
            case RankColumnType.PERIOD:
                valA = ctxA.data.period;
                valB = ctxB.data.period;
                break;
            case RankColumnType.SUPERGROUP_OVERALL_RANK:
                valA = ctxA.data.superGroupOverallRank;
                valB = ctxB.data.superGroupOverallRank;
                valA = !valA ? valA : Number(valA.split('/')[0]);
                valB = !valB ? valB : Number(valB.split('/')[0]);
                break;
            case RankColumnType.SITE_SUPERGROUP_RANK:
                valA = ctxA.data.siteSuperGroupRank;
                valB = ctxB.data.siteSuperGroupRank;
                valA = !valA ? valA : Number(valA.split('/')[0]);
                valB = !valB ? valB : Number(valB.split('/')[0]);
                break;
            case RankColumnType.ROLE_OVERALL_RANK:
                valA = ctxA.data.roleOverallRank;
                valB = ctxB.data.roleOverallRank;
                valA = !valA ? valA : Number(valA.split('/')[0]);
                valB = !valB ? valB : Number(valB.split('/')[0]);
                break;
            case RankColumnType.ROLE_BY_REGION_RANK:
                valA = ctxA.data.roleByRegionRank;
                valB = ctxB.data.roleByRegionRank;
                valA = !valA ? valA : Number(valA.split('/')[0]);
                valB = !valB ? valB : Number(valB.split('/')[0]);
                break;
            case RankColumnType.PEER_GROUP_OVERALL_RANK:
                valA = ctxA.data.peerGroupOverallRank;
                valB = ctxB.data.peerGroupOverallRank;
                valA = !valA ? valA : Number(valA.split('/')[0]);
                valB = !valB ? valB : Number(valB.split('/')[0]);
                break;
            case RankColumnType.LOB_OVERALL_RANK:
                valA = ctxA.data.lobOverallRank;
                valB = ctxB.data.lobOverallRank;
                valA = !valA ? valA : Number(valA.split('/')[0]);
                valB = !valB ? valB : Number(valB.split('/')[0]);
                break;
            default:
                console.error('invalid column');
                return Number.MIN_SAFE_INTEGER;
        }
        if (mode === 'number') {
            valA = valA == null || valA === '-' || isNaN(valA) ? null : valA;
            valB = valB == null || valB === '-' || isNaN(valB) ? null : valB;
        } else {
            valA = valA == null ? '' : valA;
            valB = valB == null ? '' : valB;
        }
        if (mode === 'number') {
            if (valA === valB) {
                return 0;
            } else if (valA === null) {
                return 1;
            } else if (valB === null) {
                return -1;
            } else if (def.order === SortOrder.ASC) {
                return valA < valB ? -1 : 1;
            } else {
                return valA < valB ? 1 : -1;
            }
        } else {
            if (def.order === SortOrder.ASC) {
                return valA.localeCompare(valB);
            } else {
                return valB.localeCompare(valA);
            }
        }
    } catch (e) {
        console.log(e);
        return Number.MIN_SAFE_INTEGER;
    }
}

export function applyRankSorting(
    rows: Array<Row<RowContext, RankCellContext>>,
    columns: Array<Column<RankColumnContext>>,
    sortDef: Sort,
): void {
    if (sortDef) {
        const column = columns.find((e) => e.id === sortDef.columnId);
        rows.sort((a, b) => {
            const cellA = a.cells.find((l) => l.columnId === column.id);
            const cellB = b.cells.find((l) => l.columnId === column.id);
            return compareDefRank(cellA, cellB, a, b, sortDef);
        });
    }
}

export function applyContributionSorting(
    rows: Array<Row<RowContext, CellContext>>,
    columns: Array<Column<ColumnContext>>,
    sortDef: Sort,
): Array<Row<RowContext, CellContext>> {
    if (sortDef) {
        return rows.sort((a, b) => {
            const cellA = a.cells.find((l) => l.columnId === sortDef.columnId);
            const cellB = b.cells.find((l) => l.columnId === sortDef.columnId);
            return compareDefContribution(cellA, cellB, a, b, sortDef);
        });
    } else {
        return null;
    }
}

export function applyScorecardSorting(
    rows: Array<Row<RowContext, CellContext>>,
    columns: Array<Column<ColumnContext>>,
    sortDef: Sort,
): Array<Row<RowContext, CellContext>> {
    if (sortDef) {
        return rows.sort((a, b) => {
            const cellA = a.cells.find((l) => l.columnId === sortDef.columnId);
            const cellB = b.cells.find((l) => l.columnId === sortDef.columnId);
            return compareDefScorecard(cellA, cellB, a, b, sortDef);
        });
    } else {
        return null;
    }
}

export function applySGFiltering<TCellContext>(
    appliedFilterList: FilterPillar[],
    rows: Array<Row<RowContext, TCellContext>>,
    superGroupIdAccessor: (cell: Cell<TCellContext>) => number,
): Array<Row<RowContext, TCellContext>> {
    const SuperGroupSelectedIds: any[] = [];
    if (
        !appliedFilterList ||
        !appliedFilterList.find(
            (x) =>
                x.isSelected === true &&
                x.category === FilterCategory.SUPER_GROUP,
        )
    ) {
        // eslint-disable-next-line no-return-assign
        rows.forEach((r) => (r.hidden = false));
    } else {
        appliedFilterList.forEach((appliedItem) => {
            if (appliedItem.isSelected === true) {
                SuperGroupSelectedIds.push(appliedItem.value);
            }
        });
        // eslint-disable-next-line no-return-assign
        rows.forEach(
            (r) =>
                (r.hidden = !r.cells.some((c) => {
                    return SuperGroupSelectedIds.includes(
                        superGroupIdAccessor(c),
                    );
                })),
        );
    }
    return rows;
}

export function applySGFilteringOpposite<TCellContext>(
    superGroupId: number,
    rows: Array<Row<RowContext, TCellContext>>,
    superGroupIdAccessor: (cell: Cell<TCellContext>) => number,
): Array<Row<RowContext, TCellContext>> {
    if (superGroupId === -1) {
        // eslint-disable-next-line no-return-assign
        rows.forEach((r) => (r.hidden = false));
    } else {
        // eslint-disable-next-line no-return-assign
        rows.forEach(
            (r) =>
                (r.hidden = r.cells.some(
                    (c) => superGroupIdAccessor(c) === superGroupId,
                )),
        );
    }
    return rows;
}

export function applySGScorecardColumnFiltering(
    filterDefAppliedList: FilterPillar[],
    rows: Array<Row<RowContext>>,
    columns: Array<Column<ColumnContext>>,
): Array<Column<ColumnContext>> {
    const colTypes = [
        ColumnType.WEIGHTED,
        ColumnType.MODIFIER,
        ColumnType.QUALIFIER,
        ColumnType.CONDITION,
        ColumnType.FYI,
    ];

    columns
        .filter((c) => colTypes.includes(c.ctx.type))
        .forEach((c) => {
            c.hidden =
                c.ctx.isExplicitlyHiddenForPillars ||
                (c.ctx.isExplicitlyHiddenForSuperGroup ? false : c.hidden);
            c.ctx.isExplicitlyHiddenForSuperGroup = undefined;
            c.children.forEach((cc) => {
                if (cc.ctx.isExplicitlyHiddenForPillars) {
                    cc.hidden = true;
                } else if (cc.ctx.isExplicitlyHiddenForSuperGroup) {
                    cc.hidden =
                        cc.ctx.hasExpandableSibling &&
                        !cc.ctx.isExplicitlyShown;
                }
                cc.ctx.isExplicitlyHiddenForSuperGroup = undefined;
            });
        });

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        filterDefAppliedList &&
        filterDefAppliedList.find((x) => x.value === -1)
    ) {
        return columns;
    }

    const visibleRows = rows.filter((r) => !r.hidden);
    if (visibleRows.length === 0) {
        return columns;
    }

    const dat = visibleRows[0].cells[0].ctx.data[0];
    const colMetricMap = {
        [ColumnType.WEIGHTED]: dat.weightedMetrics
            ? dat.weightedMetrics.map((e) => e.metricId)
            : [],
        [ColumnType.MODIFIER]: dat.modifierMetrics
            ? dat.modifierMetrics.map((e) => e.metricId)
            : [],
        [ColumnType.QUALIFIER]: dat.qualifierMetrics
            ? dat.qualifierMetrics.map((e) => e.metricId)
            : [],
        [ColumnType.CONDITION]: dat.conditionMetrics
            ? dat.conditionMetrics.map((e) => e.metricId)
            : [],
        [ColumnType.FYI]: dat.fyiMetrics
            ? dat.fyiMetrics.map((e) => e.metricId)
            : [],
    };

    colTypes.forEach((ct) => {
        columns
            .filter(
                (c) => c.ctx.type === ct && !c.ctx.isExplicitlyHiddenForPillars,
            )
            .forEach((c) => {
                c.children
                    .filter((cc) => !cc.ctx.isExplicitlyHiddenForPillars)
                    .forEach((cc) => {
                        const metricId = parseInt(cc.id.split('-')[1]);
                        if (!colMetricMap[ct].includes(metricId)) {
                            cc.hidden = false;
                            cc.ctx.isExplicitlyHiddenForSuperGroup = true;
                        }
                    });

                if (c.children.every((cc) => cc.hidden)) {
                    c.hidden = false;
                    c.ctx.isExplicitlyHiddenForSuperGroup = true;
                }
            });
    });

    return columns;
}

export function applyPillarFiltering(
    payload: FilterPillar[],
    columns: Array<Column<ColumnContext>>,
    filterPillars: FilterPillar[],
): void {
    const flat = flattenArrayWithObj(columns, 'children');

    filterPillars.forEach((item) => {
        const col = flat.find((e) => e.id === item.columnId);
        if (!col) {
            return;
        }
        col.width = null;
        col.hidden = !payload ? false : !item.isSelected;
        col.ctx.isExplicitlyHiddenForPillars = col.hidden;
        flattenArrayWithObj(col.children, 'children').forEach((e) => {
            e.width = null;
            if (payload && !item.isSelected) {
                e.hidden = true;
            } else {
                e.hidden =
                    e.ctx.hasExpandableSibling && !e.ctx.isExplicitlyShown;
            }
            e.ctx.isExplicitlyHiddenForPillars = e.hidden;
        });
    });
}

export function applyDefaultContributionSorting(
    rows: Array<Row<RowContext, CellContext>>,
): Array<Row<RowContext>> {
    return [...rows].sort((a, b) => {
        const cellA = a.cells.find(
            (l) => l.columnId === ColumnType.USER_NAME.toString(),
        );
        const cellB = b.cells.find(
            (l) => l.columnId === ColumnType.USER_NAME.toString(),
        );
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const valA1 = cellA.ctx.data[0]['userName'];
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const valB1 = cellB.ctx.data[0]['userName'];
        return valA1.localeCompare(valB1);
    });
}

export function applyDefaultRankSorting(
    rows: Array<Row<RowContext, RankCellContext>>,
): Array<Row<RowContext>> {
    return [...rows].sort((a, b) => {
        const cellA = a.cells.find(
            (l) => l.columnId === RankColumnType.SUPERGROUP_NAME.toString(),
        );
        const cellB = b.cells.find(
            (l) => l.columnId === RankColumnType.SUPERGROUP_NAME.toString(),
        );
        const valA1 = cellA.ctx.data.subSuperGroupName;
        const valB1 = cellB.ctx.data.subSuperGroupName;
        const valA2 = cellA.ctx.data.overallScore;
        const valB2 = cellB.ctx.data.overallScore;
        if (valA1.localeCompare(valB1) === 0) {
            return valB2 - valA2;
        }
        return valA1.localeCompare(valB1);
    });
}

export function applyDefaultScorecardSorting(
    rows: Array<Row<RowContext, CellContext>>,
): Array<Row<RowContext>> {
    return rows.sort((a, b) => {
        const cellA = a.cells.find(
            (l) => l.columnId === ColumnType.SUPERGROUP_NAME.toString(),
        );
        const cellB = b.cells.find(
            (l) => l.columnId === ColumnType.SUPERGROUP_NAME.toString(),
        );

        // eslint-disable-next-line @typescript-eslint/dot-notation
        const valA1 = cellA.ctx.data[0]['superGroup'];
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const valB1 = cellB.ctx.data[0]['superGroup'];

        // eslint-disable-next-line @typescript-eslint/dot-notation
        const valA2 = cellA.ctx.data[0]['overallScore'];
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const valB2 = cellB.ctx.data[0]['overallScore'];

        // eslint-disable-next-line @typescript-eslint/dot-notation
        const valA3 = cellA.ctx.data[0]['directReport'];
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const valB3 = cellB.ctx.data[0]['directReport'];

        if (valA1.localeCompare(valB1) === 0) {
            if (valA2 && valB2 && valA2 === valB2) {
                return valA3.localeCompare(valB3);
            } else if (valA2 && valB2) {
                return valB2 - valA2;
            } else {
                return valA3.localeCompare(valB3);
            }
        } else {
            return valA1.localeCompare(valB1);
        }
    });
}

export function sortDirectReportRankGrid(
    columnId: string,
    keepOrder: boolean,
    columns: Array<Column<RankColumnContext>>,
    rows: Array<Row<RowContext>>,
    sortDefs: Sort[],
): { rows: Array<Row<RowContext>>; sortDefs: Sort[] } {
    const column = columns.find((e) => e.id === columnId);
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const sortDef =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        sortDefs && sortDefs.length ? sortDefs[sortDefs.length - 1] : null;
    const order =
        sortDefs.length > 0
            ? sortDefs[sortDefs.length - 1].order
            : SortOrder.ASC;
    const defaultSortDef =
        columnId === '-1'
            ? { columnId: sortDefs[sortDefs.length - 1].columnId, order }
            : { columnId: column.ctx.type.toString(), order };
    const nextSortDefs = sortOrderWithDefault(
        columns,
        column,
        sortDef,
        defaultSortDef,
        keepOrder,
    );
    applyRankSorting(
        rows,
        columns,
        nextSortDefs.filter((s) => s.columnId === columnId)[0],
    );
    return { rows: [...rows], sortDefs: nextSortDefs };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface RankColumns {
    id: string;
    name: string;
}

const ranks: Array<{ id: string; value: (r: DirectReportRankDisplay) => any }> =
    [
        {
            id: String(RankColumnType.SITE_SUPERGROUP_RANK),
            value: (r) => r.siteSuperGroupRank,
        },
        {
            id: String(RankColumnType.ROLE_OVERALL_RANK),
            value: (r) => r.roleOverallRank,
        },
        {
            id: String(RankColumnType.ROLE_BY_REGION_RANK),
            value: (r) => r.roleByRegionRank,
        },
        {
            id: String(RankColumnType.SUPERGROUP_OVERALL_RANK),
            value: (r) => r.superGroupOverallRank,
        },
        {
            id: String(RankColumnType.PEER_GROUP_OVERALL_RANK),
            value: (r) => r.peerGroupOverallRank,
        },
        {
            id: String(RankColumnType.LOB_OVERALL_RANK),
            value: (r) => r.lobOverallRank,
        },
    ];

export function filterDirectReportRankGrid(
    columns: Array<Column<RankColumnContext>>,
    rows: Array<Row<RowContext>>,
    sortDefs: Sort[],
    filterDefAppliedList: FilterPillar[],
    filterDefs: IOptionScorecard[],
): {
    columns: Array<Column<RankColumnContext>>;
    rows: Array<Row<RowContext>>;
    sortDefs: Sort[];
    filterDefs: IOptionScorecard[];
} {
    const nextFilterDefs = [...(filterDefs || [])];
    // eslint-disable-next-line no-return-assign
    nextFilterDefs.forEach((e) => (e.isSelected = false));
    rows = applySGFiltering<RankCellContext>(
        filterDefAppliedList,
        rows,
        (c) => c.ctx.data.subSuperGroupId,
    );
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    filterDefAppliedList &&
        filterDefAppliedList.forEach((filterDefItem) => {
            const pillar = filterDefs.find(
                (p) => p.value === filterDefItem.value,
            );
            if (pillar) {
                pillar.isSelected = filterDefItem.isSelected;
            }
        });
    // eslint-disable-next-line no-return-assign
    columns.forEach((i) => (i.hidden = false));
    const columFilter = [];
    rows.filter((r) => r.hidden === false).forEach((r) => {
        columFilter.push(r.cells[0].ctx.data);
    });

    ranks.forEach((r) => {
        if (columFilter.every((c) => !r.value(c))) {
            const columnToHide = columns.find((col) => col.id === r.id);
            if (columnToHide) {
                columnToHide.hidden = true;
            }
        }
    });
    return {
        columns: [...columns],
        rows: [...rows],
        sortDefs,
        filterDefs: nextFilterDefs,
    };
}

export function sortDirectReportContributionGrid(
    columnId: string,
    columns: Array<Column<ColumnContext>>,
    rowsTop: Array<Row<RowContext>>,
    rows: Array<Row<RowContext>>,
    sortDefs: Sort[],
): {
    rowsTop: Array<Row<RowContext>>;
    rows: Array<Row<RowContext>>;
    sortDefs: Sort[];
} {
    const columnChildren = columns.filter((e) =>
        e.children.find((ch) => ch.id === columnId),
    )[0].children;
    const column = columnChildren.find((ch) => ch.id === columnId);
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const sortDef =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        sortDefs && sortDefs.length
            ? sortDefs.filter((s) => s.columnId === columnId)[0]
            : null;
    const defaultSortDef = {
        columnId: ColumnType.USER_NAME.toString(),
        order: SortOrder.ASC,
    };
    const nextSortDefs = sortOrderWithDefault(
        columns,
        column,
        sortDef,
        defaultSortDef,
    );
    const rowsComb = [...(rowsTop || []), ...(rows || [])];
    const sortedRows = applyContributionSorting(
        rowsComb,
        columns,
        nextSortDefs.filter((s) => s.columnId === columnId)[0],
    );
    const nextRowsPair = distillContributionRows(sortedRows);
    return { ...nextRowsPair, sortDefs: nextSortDefs };
}

export function sortDirectReportScorecardGrid(
    columnId: string,
    keepOrder: boolean,
    columns: Array<Column<ColumnContext>>,
    rows: Array<Row<RowContext>>,
    sortDefs: Sort[],
): { rows: Array<Row<RowContext>>; sortDefs: Sort[] } {
    const columnChildren = columns.filter((e) =>
        e.children.find((ch) => ch.id === columnId),
    )[0].children;
    const column = columnChildren.find((ch) => ch.id === columnId);
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const usedSortDef =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        sortDefs && sortDefs.length
            ? sortDefs.filter((s) => s.columnId === columnId)[0]
            : null;
    const defaultSortDef = {
        columnId: ColumnType.USER_NAME.toString(),
        order: SortOrder.ASC,
    };
    const nextSortDefs = sortOrderWithDefault(
        columns,
        column,
        usedSortDef,
        defaultSortDef,
    );
    const sortedRows = applyScorecardSorting(
        rows,
        columns,
        nextSortDefs.filter((s) => s.columnId === columnId)[0],
    );
    return { rows: sortedRows, sortDefs: nextSortDefs };
}

export function filterDirectReportContributionGrid(
    rowsTop: Array<Row<RowContext>>,
    rows: Array<Row<RowContext>>,
    sortDefs: Sort[],
    filterDefAppliedList: FilterPillar[],
    filterDefs: IOptionScorecard[],
): {
    rowsTop: Array<Row<RowContext>>;
    rows: Array<Row<RowContext>>;
    sortDefs: Sort[];
    filterDefs: IOptionScorecard[];
} {
    const nextFilterDefs = [...(filterDefs || [])];
    // eslint-disable-next-line no-return-assign
    nextFilterDefs.forEach((e) => (e.isSelected = false));

    const rowsComb = [...(rowsTop || []), ...(rows || [])];
    const nextRowsComb = applySGFiltering(
        filterDefAppliedList,
        rowsComb,
        (c) => c.ctx.data[0].superGroupId,
    );
    const nextRowsPair = distillContributionRows(nextRowsComb);

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    filterDefAppliedList &&
        filterDefAppliedList.forEach((filterDefItem) => {
            const pillar = filterDefs.find(
                (p) => p.value === filterDefItem.value,
            );
            if (pillar) {
                pillar.isSelected = filterDefItem.isSelected;
            }
        });
    return { ...nextRowsPair, sortDefs, filterDefs: nextFilterDefs };
}

export function filterGridPillar(
    payload: FilterPillar[],
    columns: Array<Column<ColumnContext>>,
    filterPillars: FilterPillar[],
    defaultColumnIdOrder: string[],
): { columns: Array<Column<ColumnContext>>; filterPillars: FilterPillar[] } {
    const nextFilterPillars = filterPillars || [];
    // eslint-disable-next-line no-return-assign
    nextFilterPillars.forEach((p) => (p.isSelected = false));

    if (payload) {
        payload.forEach((deltaItem) => {
            const pillar = nextFilterPillars.find(
                (p) => p.columnId === deltaItem.columnId,
            );
            if (pillar) {
                pillar.isSelected = deltaItem.isSelected;
            }
        });
    }

    const nextColumns = [...columns];
    applyPillarFiltering(payload, nextColumns, nextFilterPillars);

    if (!payload) {
        /* TODO remove. this was originally for ordering the pillars in the order that they picked on the ui, but that seems to not be required anymore */
        nextColumns.sort((a, b) => {
            return (
                defaultColumnIdOrder.indexOf(a.id) -
                defaultColumnIdOrder.indexOf(b.id)
            );
        });
    } else {
        /* TODO remove. this was originally for ordering the pillars in the order that they picked on the ui, but that seems to not be required anymore */
        nextFilterPillars.forEach((item) => {
            if (!item.isSelected) {
                return;
            }
            const index = nextColumns.findIndex((c) => c.id === item.columnId);
            if (index !== -1) {
                const c = nextColumns.splice(index, 1)[0];
                nextColumns.push(c);
            }
        });
    }

    return { columns: nextColumns, filterPillars: nextFilterPillars };
}

export function syncScorecardGrid(
    tplConfig: TemplateConfig,
    header: ScorecardDto,
    data: DirectReportScorecardResult[],
    sortDefs: Sort[],
): DirectReportScorecardGrid {
    header = header || createEmpty();
    const clearData = clearScorecardGridData(data);
    const nextColumns = makeScorecardColumns(clearData, data);
    let nextRows = makeScorecardRows(nextColumns, clearData);
    nextRows = applyDefaultScorecardSorting(nextRows);
    const nextFilterPillar = generateMetricPillarList(nextColumns);
    const newFilterDef = generateSGList(nextRows, GridType.Direct_Reports_View);
    if (!sortDefs) {
        sortDefs = [];
    }
    return {
        scorecardId: header.scorecardId,
        superGroupName: header.groupName,
        columns: nextColumns,
        rows: nextRows,
        sortDefs,
        filterPillar: nextFilterPillar,
        filterDef: newFilterDef,
        defaultColumnIdOrder: nextColumns.map((e) => e.id),
    };
}

function clearScorecardGridData(
    data: DirectReportScorecardResult[],
): DirectReportScorecardDisplay[] {
    const unique = Array<string>();

    for (const d of data) {
        const key = `${d.subScorecardId}**${d.subUserId}**${d.subSuperGroupId}**${d.subStartDate}**${d.subEndDate}`;
        if (!unique.includes(key)) {
            unique.push(key);
        }
    }

    const drScorecardDisplay = new Array<DirectReportScorecardDisplay>();
    unique.forEach((u) => {
        const params = u.split('**');
        const filtered = data.filter(
            (d) =>
                d.subScorecardId.toString() === params[0] &&
                d.subUserId.toString() === params[1] &&
                d.subSuperGroupId.toString() === params[2] &&
                d.subStartDate.toString() === params[3] &&
                d.subEndDate.toString() === params[4],
        );

        const sDate = new Date(filtered[0].subStartDate);
        const eDate = new Date(filtered[0].subEndDate);
        const p = `${
            sDate.getMonth() + 1
        }-${sDate.getDate()}-${sDate.getFullYear()} / ${
            eDate.getMonth() + 1
        }-${eDate.getDate()}-${eDate.getFullYear()}`;

        const rawWMetrics = filtered.filter(
            (f) => f.pillarId === DrsColumnType.WEIGHTED_METRICS,
        );
        const rawFyiMetrics = filtered.filter(
            (f) => f.pillarId === DrsColumnType.FYI_METRICS,
        );
        const rawCondMetrics = filtered.filter(
            (f) => f.pillarId === DrsColumnType.CONDITION_METRICS,
        );
        const rawModMetrics = filtered.filter(
            (f) => f.pillarId === DrsColumnType.MODIFIER_METRICS,
        );
        const rawQMetrics = filtered.filter(
            (f) => f.pillarId === DrsColumnType.QUALIFIER_METRICS,
        );
        const nDRScorecardDisplay: DirectReportScorecardDisplay = {
            directReport: filtered[0].subUserName,
            scorecardId: filtered[0].subScorecardId,
            userId: filtered[0].subUserId,
            superGroupId: filtered[0].subSuperGroupId,
            superGroup: filtered[0].subSuperGroupName,
            period: p,
            overallScore: filtered[0].subAdjustedPoints,
            weightedMetrics: generateDRScorecardMetric(rawWMetrics),
            fyiMetrics: generateDRScorecardMetric(rawFyiMetrics),
            conditionMetrics: generateDRScorecardMetric(rawCondMetrics),
            modifierMetrics: generateDRScorecardMetric(rawModMetrics),
            qualifierMetrics: generateDRScorecardMetric(rawQMetrics),
        };
        drScorecardDisplay.push(nDRScorecardDisplay);
    });
    return drScorecardDisplay;
}

function clearContributionGridData(
    data: DirectReportContributionResult[],
): DirectReportContributionDisplay[] {
    const unique = [];
    const drContributionDisplay: DirectReportContributionDisplay[] = [];

    for (const d of data) {
        const key = `${d.userId}**${d.superGroupName}**${d.startDate}**${d.endDate}`;
        if (!unique.includes(key)) {
            unique.push(key);
        }
    }
    for (const u of unique) {
        const params = u.split('**');
        const filtered = data.filter(
            (d) =>
                d.userId.toString() === params[0] &&
                String(d.superGroupName) === params[1] &&
                d.startDate.toString() === params[2] &&
                d.endDate.toString() === params[3],
        );
        if (filtered.length === 0) {
            continue;
        }
        const sDate = new Date(filtered[0].startDate);
        const eDate = new Date(filtered[0].endDate);
        const p = `${
            sDate.getMonth() + 1
        }-${sDate.getDate()}-${sDate.getFullYear()} / ${
            eDate.getMonth() + 1
        }-${eDate.getDate()}-${eDate.getFullYear()}`;
        const nDRContributionDisplay: DirectReportContributionDisplay = {
            scorecardId: filtered[0].scorecardId,
            recordType: filtered[0].recordType,
            userId: filtered[0].userId,
            userName: filtered[0].userName,
            superGroupId: filtered[0].superGroupId,
            superGroupName: filtered[0].superGroupName,
            startDate: filtered[0].startDate,
            endDate: filtered[0].endDate,
            period: p,
            metrics: generateDRContributionMetric(filtered),
        };
        drContributionDisplay.push(nDRContributionDisplay);
    }
    return drContributionDisplay;
}

function generateDRScorecardMetric(
    data: DirectReportScorecardResult[],
): DRScorecardMetric[] {
    const result = new Array<DRScorecardMetric>();
    data.forEach((d) => {
        const rank =
            d.rank != null && d.maxRank != null
                ? `${d.rank}/${d.maxRank}`
                : '-';
        const nDRScorecardMetric: DRScorecardMetric = {
            pillarId: d.pillarId,
            metricId: d.metricId,
            identifier: `${d.pillarId}-${d.metricId}`,
            metricName: d.metricName,
            actual: d.actual,
            target: d.target,
            percentToTarget: d.targetPercent,
            weight: d.weight,
            pointBand: d.pointsBand,
            adjustedPoints: d.adjustedPoints,
            isInEffect: d.isInEffect,
            rank,
        };
        result.push(nDRScorecardMetric);
    });
    return result;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-unused-vars
function generateUniqueMetricList(
    data: DirectReportScorecardDisplay[],
    metricType: string,
) {
    const unique = Array<string>();
    const result = Array<DRScorecardMetric>();
    for (const d of data) {
        switch (metricType) {
            case ColumnType.WEIGHTED.toString():
                d.weightedMetrics.forEach((w) => {
                    if (!unique.includes(w.metricId.toString())) {
                        unique.push(w.metricId.toString());
                        result.push(w);
                    }
                });
                break;
            case ColumnType.FYI.toString():
                d.fyiMetrics.forEach((u) => {
                    if (!unique.includes(u.metricId.toString())) {
                        unique.push(u.metricId.toString());
                        result.push(u);
                    }
                });
                break;
            case ColumnType.MODIFIER.toString():
                d.modifierMetrics.forEach((m) => {
                    if (!unique.includes(m.metricId.toString())) {
                        unique.push(m.metricId.toString());
                        result.push(m);
                    }
                });
                break;
            case ColumnType.QUALIFIER.toString():
                d.qualifierMetrics.forEach((q) => {
                    if (!unique.includes(q.metricId.toString())) {
                        unique.push(q.metricId.toString());
                        result.push(q);
                    }
                });
                break;
            case ColumnType.CONDITION.toString():
                d.conditionMetrics.forEach((c) => {
                    if (!unique.includes(c.metricId.toString())) {
                        unique.push(c.metricId.toString());
                        result.push(c);
                    }
                });
                break;
            default:
                break;
        }
    }
    return result;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function sortWeightedMetricColumns(
    data: DirectReportScorecardDisplay[],
    pillars: Pillar[],
) {
    const lookup: {
        [metricId: number]: { reportCount: number; totalWeight: number };
    } = {};
    const result = pillars.find(
        (wm) => wm.identifier === DrsColumnType.WEIGHTED_METRICS.toString(),
    )?.metrics;
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    result &&
        result.forEach((w) => {
            lookup[w.metricId] = { reportCount: 0, totalWeight: 0 };
            data.forEach((d) => {
                const index = d.weightedMetrics.findIndex(
                    (dd) => dd.metricId === w.metricId && dd.actual != null,
                );
                if (index !== -1) {
                    lookup[w.metricId].reportCount += 1;
                    lookup[w.metricId].totalWeight +=
                        d.weightedMetrics[index].weight;
                }
            });
        });

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    result &&
        result.sort(
            (a, b) =>
                lookup[b.metricId].reportCount -
                    lookup[a.metricId].reportCount ||
                lookup[b.metricId].totalWeight -
                    lookup[a.metricId].totalWeight ||
                a.metricName.localeCompare(b.metricName),
        );
    return result;
}

function makeScorecardColumns(
    data: DirectReportScorecardDisplay[],
    directReportSc: DirectReportScorecardResult[],
): Array<Column<ColumnContext>> {
    const height = 40;

    /* stickied columns need to be separate because reconstructColumns() doesn't handle single stickied child column
    while the siblings and parent are not */
    const columns: Array<Column<ColumnContext>> = [
        {
            id: '',
            height,
            sticky: 'left',
            ctx: {
                templateId: TemplateID.HEADER_GROUP,
                type: '' as any as ColumnType,
                metricName: '',
                displayOrder: 1,
            },
            children: [
                {
                    id: ColumnType.USER_NAME.toString(),
                    compareFn: compareScorecard,
                    sticky: 'left',
                    ctx: {
                        templateId: TemplateID.HEADER,
                        type: '' as any as ColumnType,
                        metricId: ColumnType.USER_NAME.toString(),
                        metricName: TranslationKeyMap._9128_DIRECTREPORTS,
                    },
                },
            ],
        },
        {
            id: '2',
            height,
            ctx: {
                templateId: TemplateID.HEADER_GROUP,
                type: '' as any as ColumnType,
                metricName: '',
                displayOrder: 2,
            },
            children: [
                {
                    id: ColumnType.SUPERGROUP_NAME.toString(),
                    compareFn: compareScorecard,
                    ctx: {
                        templateId: TemplateID.HEADER,
                        type: '' as any as ColumnType,
                        metricId: ColumnType.SUPERGROUP_NAME.toString(),
                        metricName: TranslationKeyMap._67_SUPERGROUP,
                    },
                },
                {
                    id: ColumnType.PERIOD.toString(),
                    compareFn: compareScorecard,
                    ctx: {
                        templateId: TemplateID.HEADER,
                        type: '' as any as ColumnType,
                        metricId: ColumnType.PERIOD.toString(),
                        metricName: TranslationKeyMap._773_PERIOD,
                    },
                },
                {
                    id: ColumnType.OVERALL_SCORE.toString(),
                    compareFn: compareScorecard,
                    ctx: {
                        templateId: TemplateID.HEADER,
                        type: '' as any as ColumnType,
                        metricId: ColumnType.OVERALL_SCORE.toString(),
                        metricName: TranslationKeyMap._962_OVERALL_SCORE,
                    },
                },
            ],
        },
    ];

    const foundPseudoPillars: Pillar[] = [];
    directReportSc.forEach((e) => {
        const pillarIdStr = e.pillarId.toString();
        const pseudoPillar = pseudoPillars.find(
            (ee) => ee.identifier === pillarIdStr,
        );
        if (!pseudoPillar) {
            return;
        }

        const index = foundPseudoPillars.findIndex(
            (p) => p.identifier === pillarIdStr,
        );
        if (index === -1) {
            foundPseudoPillars.push({
                identifier: pseudoPillar.identifier,
                name: pseudoPillar.name,
                shortName: null,
                initials: null,
                clientLanguageTranslationId: null,
                description: null,
                descriptionClientLanguageTranslationId: null,
                displayOrder: e.pillarOrder,
                metrics: [],
            });
        }
    });
    foundPseudoPillars.forEach((e) => {
        const scData = directReportSc.filter(
            (ee) => ee.pillarId.toString() === e.identifier,
        );
        const unique = [];
        const metrics = [];
        scData.forEach((d) => {
            if (!unique.includes(d.metricId)) {
                unique.push(d.metricId);
                metrics.push({
                    actual: d.actual,
                    adjustedPoints: d.adjustedPoints,
                    isInEffect: d.isInEffect,
                    metricId: d.metricId,
                    metricName: d.metricName,
                    percentToTarget: d.targetPercent,
                    pointBand: d.pointsBand,
                    rank: d.rank,
                    target: d.target,
                    weight: d.weight,
                });
            }
        });
        e.metrics = metrics;
    });

    const pillars = [...foundPseudoPillars];
    const weightedMetricColumns: Array<Column<ColumnContext>> = [];
    const fyiMetricColumns: Array<Column<ColumnContext>> = [];
    const conditionMetricColumns: Array<Column<ColumnContext>> = [];
    const qualifierMetricColumns: Array<Column<ColumnContext>> = [];
    const modifierMetricColumns: Array<Column<ColumnContext>> = [];

    sortWeightedMetricColumns(data, pillars);

    pillars.forEach((ps) => {
        switch (ps.identifier) {
            case String(DrsColumnType.WEIGHTED_METRICS):
                ps.metrics.forEach((wm) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    const dat =
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        directReportSc &&
                        directReportSc.find(
                            (e) =>
                                e.pillarId.toString() === ps.identifier &&
                                e.metricId === wm.metricId,
                        );
                    if (!dat) {
                        return;
                    }
                    const colId = `${DrsColumnType.WEIGHTED_METRICS.toString()}-${wm.metricId.toString()}`;
                    weightedMetricColumns.push(
                        {
                            id: colId,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: ColumnType.WEIGHTED,
                                pillarId: ps.identifier,
                                metricId: `${ps.identifier}-${dat.metricId}`,
                                metricName: dat.metricName,
                                isExpandable: true,
                            },
                            hidden: false,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.WEIGHTED.toString()}-${
                                    wm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.TARGET.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._726_TARGET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET_PERCENT}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.WEIGHTED.toString()}-${
                                    wm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.TARGET_PERCENT.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._768___TO_TARGET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.POINTS_BAND}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.WEIGHTED.toString()}-${
                                    wm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.POINTS_BAND.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._9261_POINTS_BAND,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.WEIGHT}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.WEIGHTED.toString()}-${
                                    wm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.WEIGHT.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._9256_WEIGHT,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.ADJUSTED_POINTS}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.WEIGHTED.toString()}-${
                                    wm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.ADJUSTED_POINTS.toString(),
                                pillarId: ps.identifier,
                                metricName:
                                    TranslationKeyMap._9258_ADJUSTED_POINTS,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.METRIC_RANK}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.WEIGHTED.toString()}-${
                                    wm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.RANK.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._9260_METRIC_RANK,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                    );
                });
                if (weightedMetricColumns.length > 0) {
                    columns.push({
                        id: ps.identifier.toString(),
                        height,
                        ctx: {
                            templateId: TemplateID.HEADER_GROUP,
                            type: ColumnType.WEIGHTED,
                            pillarId: ps.identifier,
                            pillarName:
                                TranslationKeyMap._9898_WEIGHTED_METRICS,
                            displayOrder: 3,
                        },
                        children: weightedMetricColumns,
                    });
                }
                break;
            case String(DrsColumnType.FYI_METRICS):
                ps.metrics.forEach((fm) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    const dat =
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        directReportSc &&
                        directReportSc.find(
                            (e) =>
                                e.pillarId.toString() === ps.identifier &&
                                e.metricId === fm.metricId,
                        );
                    if (!dat) {
                        return;
                    }
                    const colId = `${DrsColumnType.FYI_METRICS.toString()}-${fm.metricId.toString()}`;
                    fyiMetricColumns.push(
                        {
                            id: colId,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: ColumnType.FYI,
                                pillarId: ps.identifier,
                                metricId: `${ps.identifier}-${dat.metricId}`,
                                metricName: dat.metricName,
                                isExpandable: true,
                            },
                            hidden: false,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.FYI.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.TARGET.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._726_TARGET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET_PERCENT}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.FYI.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.TARGET_PERCENT.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._768___TO_TARGET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                    );
                });
                if (fyiMetricColumns.length > 0) {
                    columns.push({
                        id: ps.identifier.toString(),
                        height,
                        ctx: {
                            templateId: TemplateID.HEADER_GROUP,
                            type: ColumnType.FYI,
                            pillarId: ps.identifier,
                            pillarName: TranslationKeyMap._9894_FYI_METRICS,
                            displayOrder: 4,
                        },
                        children: fyiMetricColumns,
                    });
                }
                break;
            case String(DrsColumnType.CONDITION_METRICS):
                ps.metrics.forEach((fm) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    const dat =
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        directReportSc &&
                        directReportSc.find(
                            (e) =>
                                e.pillarId.toString() === ps.identifier &&
                                e.metricId === fm.metricId,
                        );
                    if (!dat) {
                        return;
                    }
                    const colId = `${DrsColumnType.CONDITION_METRICS.toString()}-${fm.metricId.toString()}`;
                    conditionMetricColumns.push(
                        {
                            id: colId,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: ColumnType.CONDITION,
                                pillarId: ps.identifier,
                                metricId: fm.metricId.toString(),
                                metricName: dat.metricName,
                                isExpandable: true,
                            },
                            hidden: false,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.CONDITION.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.TARGET.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._726_TARGET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET_PERCENT}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.CONDITION.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.TARGET_PERCENT.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._768___TO_TARGET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.ISINEFFECT}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.CONDITION.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.ISINEFFECT.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._9374_IN_EFFECT,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                    );
                });
                if (conditionMetricColumns.length > 0) {
                    columns.push({
                        id: ps.identifier.toString(),
                        height,
                        ctx: {
                            templateId: TemplateID.HEADER_GROUP,
                            type: ColumnType.CONDITION,
                            pillarId: ps.identifier,
                            pillarName:
                                TranslationKeyMap._9895_CONDITION_METRICS,
                            displayOrder: 5,
                        },
                        children: conditionMetricColumns,
                    });
                }
                break;
            case String(DrsColumnType.MODIFIER_METRICS):
                ps.metrics.forEach((fm) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    const dat =
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        directReportSc &&
                        directReportSc.find(
                            (e) =>
                                e.pillarId.toString() === ps.identifier &&
                                e.metricId === fm.metricId,
                        );
                    if (!dat) {
                        return;
                    }
                    const colId = `${DrsColumnType.MODIFIER_METRICS.toString()}-${fm.metricId.toString()}`;
                    modifierMetricColumns.push(
                        {
                            id: colId,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: ColumnType.MODIFIER,
                                pillarId: ps.identifier,
                                metricId: fm.metricId.toString(),
                                metricName: dat.metricName,
                                isExpandable: true,
                            },
                            hidden: false,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.MODIFIER.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.TARGET.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._726_TARGET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET_PERCENT}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.MODIFIER.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.TARGET_PERCENT.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._768___TO_TARGET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.ISINEFFECT}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.MODIFIER.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.ISINEFFECT.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._9374_IN_EFFECT,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                    );
                });
                if (modifierMetricColumns.length > 0) {
                    columns.push({
                        id: ps.identifier.toString(),
                        height,
                        ctx: {
                            templateId: TemplateID.HEADER_GROUP,
                            type: ColumnType.MODIFIER,
                            pillarId: ps.identifier,
                            pillarName:
                                TranslationKeyMap._9896_MODIFIER_METRICS,
                            displayOrder: 6,
                        },
                        children: modifierMetricColumns,
                    });
                }
                break;
            case String(DrsColumnType.QUALIFIER_METRICS):
                ps.metrics.forEach((fm) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    const dat =
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        directReportSc &&
                        directReportSc.find(
                            (e) =>
                                e.pillarId.toString() === ps.identifier &&
                                e.metricId === fm.metricId,
                        );
                    if (!dat) {
                        return;
                    }
                    const colId = `${DrsColumnType.QUALIFIER_METRICS.toString()}-${fm.metricId.toString()}`;
                    qualifierMetricColumns.push(
                        {
                            id: colId,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: ColumnType.QUALIFIER,
                                pillarId: ps.identifier,
                                metricId: fm.metricId.toString(),
                                metricName: dat.metricName,
                                isExpandable: true,
                            },
                            hidden: false,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.QUALIFIER.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.TARGET.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._726_TARGET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.TARGET_PERCENT}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.QUALIFIER.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.TARGET_PERCENT.toString(),
                                pillarId: ps.identifier,
                                metricName: TranslationKeyMap._768___TO_TARGET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                        {
                            id: `${ps.identifier}-${dat.metricId}-${ColumnType.QUALIFIERMET}`,
                            compareFn: compareScorecard,
                            ctx: {
                                templateId: TemplateID.HEADER,
                                type: `${ColumnType.QUALIFIER.toString()}-${
                                    fm.metricId
                                }` as any as ColumnType,
                                metricId: ColumnType.QUALIFIERMET.toString(),
                                pillarId: ps.identifier,
                                metricName:
                                    TranslationKeyMap._9305_ALL_QUALIFIERS_MET,
                                expandableSiblingColumnId: colId,
                                hasExpandableSibling: true,
                            },
                            hidden: true,
                        },
                    );
                });
                if (qualifierMetricColumns.length > 0) {
                    columns.push({
                        id: ps.identifier.toString(),
                        height,
                        ctx: {
                            templateId: TemplateID.HEADER_GROUP,
                            type: ColumnType.QUALIFIER,
                            pillarId: ps.identifier,
                            pillarName:
                                TranslationKeyMap._9897_QUALIFIER_METRICS,
                            displayOrder: 7,
                        },
                        children: qualifierMetricColumns,
                    });
                }
                break;

            default:
                break;
        }
    });
    return columns;
}

function makeScorecardRows(
    columns: Column[],
    data: DirectReportScorecardDisplay[],
): Array<Row<RowContext>> {
    const height = 30;
    const rows: Array<Row<RowContext>> = [];
    let cells: Array<Cell<CellContext>> = [];
    data.forEach((d, i) => {
        cells = new Array<Cell<CellContext>>();
        columns.forEach((c) => {
            cells.push(
                ...c.children.map((cc) => ({
                    columnId: cc.id,
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                    type: c.ctx.type as any as ColumnType,
                    height,
                    ctx: {
                        templateId: TemplateID.CONTENT,
                        data: [d],
                        pillarId: c.ctx.pillarId,
                    },
                })),
            );
        });
        rows.push({
            id: i.toString(),
            height,
            cells,
        });
    });
    return rows;
}

function directReportScorecardEmptyColumns(
    rows: Array<Row<RowContext>>,
    columns: Array<Column<ColumnContext>>,
    filterDefs: IOptionScorecard[],
): { emptyColumns: String[] } {
    const rowData = [];
    rows.forEach((row) => {
        if (row.hidden === false) {
            rowData.push(row.cells[0].ctx.data[0]);
        }
    });

    const pillarMetrics = [];
    columns.forEach((c) => {
        const newArr = c.children.filter((cf) => cf.ctx.isExpandable === true);
        pillarMetrics.push(...newArr.map((n) => n.id));
    });

    const uniqueMetricList = [];
    rowData.forEach((rd) => {
        uniqueMetricList.push(
            ...rd.conditionMetrics,
            ...rd.fyiMetrics,
            ...rd.modifierMetrics,
            ...rd.qualifierMetrics,
            ...rd.weightedMetrics,
        );
    });

    const metricsToCheck = [];
    pillarMetrics.forEach((pm) => {
        const compareMetrics = uniqueMetricList.filter(
            (x) => x.identifier === pm,
        );
        if (compareMetrics.length > 0) {
            metricsToCheck.push(compareMetrics[0].identifier);
        }
        if (filterDefs.every((f) => f.isSelected !== false)) {
            columns.find((c) => c.id === pm.substring(0, 4)).hidden = false;
            columns
                .find((c) => c.id === pm.substring(0, 4))
                .children.find((x) => x.id === pm).hidden = false;
        }
    });

    const emptyColumns = pillarMetrics.filter(
        (x) => !metricsToCheck.includes(x),
    );
    return { emptyColumns };
}

export function filterDirectReportScorecardGrid(
    rows: Array<Row<RowContext>>,
    columns: Array<Column<ColumnContext>>,
    sortDefs: Sort[],
    filterDefAppliedList: FilterPillar[],
    filterDefs: IOptionScorecard[],
): {
    rows: Array<Row<RowContext>>;
    columns: Array<Column<ColumnContext>>;
    sortDefs: Sort[];
    filterDefs: IOptionScorecard[];
} {
    const nextFilterDefs = [...(filterDefs || [])];
    // eslint-disable-next-line no-return-assign
    nextFilterDefs.forEach((e) => (e.isSelected = false));
    rows = applySGFiltering(
        filterDefAppliedList,
        rows,
        (c) => c.ctx.data[0].superGroupId,
    );
    columns = applySGScorecardColumnFiltering(
        filterDefAppliedList,
        rows,
        columns,
    );

    const drsMetrics = directReportScorecardEmptyColumns(
        rows,
        columns,
        filterDefs,
    );
    const { emptyColumns } = drsMetrics;

    emptyColumns.forEach((i) => {
        const pillarId =
            columns.find((c) => c.id === i.substring(0, 4)) ?? null;
        if (pillarId === null) return;

        if (filterDefs.every((f) => f.isSelected === false)) {
            pillarId.hidden = false;
        }

        if (filterDefAppliedList === null) {
            pillarId.hidden = false;
        }

        const pillarChildren = pillarId.children?.find((x) => x.id === i);
        if (!pillarChildren) return;

        pillarChildren.hidden = true;
        if (pillarId.children.every((x) => x.hidden === true)) {
            pillarId.hidden = true;
        }
    });
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    filterDefAppliedList &&
        filterDefAppliedList.forEach((filterDefItem) => {
            const pillar = filterDefs.find(
                (p) => p.value === filterDefItem.value,
            );
            if (pillar) {
                pillar.isSelected = filterDefItem.isSelected;
            }
        });

    return {
        rows: [...rows],
        columns: [...columns],
        sortDefs,
        filterDefs: nextFilterDefs,
    };
}
